import { lazy as reactLazy, type ComponentType, type LazyExoticComponent } from 'react'

/**
 * lazyWithRetry — drop-in replacement for React.lazy that survives deploys.
 *
 * Root cause of ~90% of the rows in the admin errors page
 * ("Failed to fetch dynamically imported module", "'text/html' is not a
 * valid JavaScript MIME type", "Cannot read properties of undefined
 * (reading 'default')"):
 *
 *   1. User opens the site → browser caches index.html referencing
 *      `/assets/tool-detail-page-ABC123.js`.
 *   2. We deploy → that hashed file no longer exists on the CDN.
 *   3. User navigates → `import()` 404s (or returns index.html with a
 *      text/html MIME), React.lazy resolves `undefined`, and React
 *      crashes reading `.default` off it — the classic React #185.
 *
 * This wrapper retries the import once (transient network blip), and on
 * a second failure forces a single full-page reload when a newer build
 * is likely — then gives up and rethrows so the RouteErrorBoundary
 * shows its friendly "new version" screen instead of a blank page.
 *
 * The reload is guarded by sessionStorage so a genuinely broken chunk
 * can't trap the user in an infinite reload loop: reload at most once
 * per route per tab session.
 */
export function lazyWithRetry<T extends ComponentType<any>>(
  importer: () => Promise<{ default: T }>,
  routeId?: string,
): LazyExoticComponent<T> {
  return reactLazy(async () => {
    const key = `__lazy_retry_${routeId ?? 'route'}`
    try {
      return await importer()
    } catch (firstErr) {
      // One retry after a short pause — covers transient CDN/network blips.
      await new Promise((r) => setTimeout(r, 800))
      try {
        return await importer()
      } catch (secondErr) {
        // Likely a stale-asset deploy: bust the cached index.html once.
        try {
          if (!window.sessionStorage.getItem(key)) {
            window.sessionStorage.setItem(key, '1')
            window.location.reload()
            // Return a never-resolving promise so React stays in Suspense
            // until the reload happens instead of rendering the boundary.
            await new Promise(() => {})
          }
        } catch {
          /* storage blocked — fall through to rethrow */
        }
        throw secondErr
      }
    }
  })
}

/**
 * True when an error value looks like a stale-chunk / deploy artifact
 * rather than a real code bug: dynamic-import 404s, HTML-served-as-JS,
 * or React #185 (`.default` of undefined after a failed lazy load).
 */
export function isChunkLikeError(message: string): boolean {
  const m = message.toLowerCase()
  return (
    m.includes('chunk') ||
    m.includes('failed to fetch dynamically imported module') ||
    m.includes('error loading dynamically imported module') ||
    m.includes('importing a module script failed') ||
    m.includes('is not a valid javascript mime type') ||
    // React #185 — lazy() resolved undefined after a failed import.
    m.includes("reading 'default'") ||
    m.includes('reading "default"')
  )
}
